import {
  PersonioConfig,
  PersonioIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/personio';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { removePersonioConfig, usePersonioConfig } from './service';

const NAME = 'Personio';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const hasEmptyProp = (obj: PersonioConfig) => {
  for (let k in obj) {
    if (!k.trim()) {
      return true;
    }
  }

  return false;
};

const SetupDialog = ({
  config,
  open,
  onClose,
  onFailure,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<PersonioIntegrationConfig>) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<PersonioConfig>({
    orgId: config?.data.orgId || '',
    clientId: config?.data.clientId || '',
    clientSecret: masked(config?.data.clientSecret || ''),
  });
  const [disabled, setDisabled] = useState<boolean>(true);

  useEffect(() => {
    if (config?.data) {
      setState({
        orgId: config.data.orgId || '',
        clientId: config.data.clientId || '',
        clientSecret: masked(config.data.clientSecret),
      });

      setDisabled(!!config?.data.clientId && !!config?.data.clientSecret);
    }
  }, [config?.data]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Connect Personio and Dado to import your employee data')}
        </Alert>

        <FormRow>
          <CustomInputLabel label={t('Client ID')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientId}
              disabled={disabled}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('API Secret')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientSecret}
              disabled={disabled}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientSecret: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
          { disabled &&  <Alert severity="warning" icon={false}>
          {t('Secrets are encrypted. To update, please remove the current credentials first.')}
        </Alert>
        }
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removePersonioConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={hasEmptyProp(config.data)}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  CF.personio.verifyCredentialsAndStore,
                  state
                )
                  .then(onSuccessfulIntegration)
                  .catch(onFailure)
              }
              disabled={
                hasEmptyProp(state) ||
                (
                  state.clientId === config.data.clientId &&
                  state.clientSecret.indexOf('●') !== -1)
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const PersonioSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = usePersonioConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      name={NAME}
      functionalities={functionalities}
      logoUrl="/images/integrations-logos/personio.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
